import React, { useState } from 'react';
import { User, Mail, Building, MessageSquare, ArrowRight, CheckCircle, Sparkles, Phone, Clock, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import Layout from '../components/layout/Layout';
import emailjs from '@emailjs/browser';

interface TerminFormData {
  email: string;
  phone: string;
  topic: string;
  message?: string;
}

const TerminBuchen: React.FC = () => {
  const navigate = useNavigate();

  // Initialize EmailJS and scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
    
    // Initialize EmailJS
    try {
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'VHnAhfqToQZLGoyYr';
      emailjs.init(publicKey);
      console.log('EmailJS initialized with public key:', publicKey);
      console.log('All env vars:', {
        serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_7lo8k5t',
        templateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_3q4vvgo',
        publicKey: publicKey
      });
    } catch (error) {
      console.error('Failed to initialize EmailJS:', error);
    }
  }, []);
  
  const [formData, setFormData] = useState<TerminFormData>({
    email: '',
    phone: '',
    topic: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const topics = [
    { 
      value: 'app-entwicklung-ki', 
      label: 'App-Entwicklung mit KI-Integration',
      description: 'Mobile Apps & Web-Apps mit intelligenten Features'
    },
    { 
      value: 'webseite-digital', 
      label: 'Premium Webseite & Digital Marketing',
      description: 'Moderne Webpräsenz mit Conversion-Optimierung'
    },
    { 
      value: 'prozessautomatisierung', 
      label: 'Prozessautomatisierung',
      description: 'Arbeitsabläufe digitalisieren und automatisieren'
    },
    { 
      value: 'ki-loesungen', 
      label: 'KI-Lösungen für Unternehmen',
      description: 'Chatbots, KI-Assistenten und intelligente Workflows'
    },
    { 
      value: 'ecommerce', 
      label: 'E-Commerce & Online Shop',
      description: 'Verkaufsstarke Online-Shops mit KI-Features'
    },
    { 
      value: 'digitale-transformation', 
      label: 'Digitale Transformation',
      description: 'Ganzheitliche Digitalisierung des Unternehmens'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const canSubmit = formData.email && formData.topic;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {

      // Speichern der Buchung
      const existingTermine = JSON.parse(localStorage.getItem('oetterliapps_termine') || '[]');
      
      const newTermin = {
        id: `premium-booking-${Date.now()}`,
        email: formData.email,
        phone: formData.phone,
        topic: formData.topic,
        message: formData.message,
        status: 'neu',
        createdAt: new Date().toISOString(),
        source: 'premium_booking_page',
        value: 297
      };

      const updatedTermine = [...existingTermine, newTermin];
      localStorage.setItem('oetterliapps_termine', JSON.stringify(updatedTermine));

      // Prepare email template parameters
      const templateParams = {
        to_email: 'patrick.oetterli@gmail.com',
        from_email: formData.email,
        from_phone: formData.phone || 'Nicht angegeben',
        topic_label: topics.find(t => t.value === formData.topic)?.label || formData.topic,
        message: formData.message || 'Keine zusätzliche Nachricht',
        booking_id: newTermin.id,
        created_at: format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })
      };

      // Send email via EmailJS
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_7lo8k5t';
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_3q4vvgo';
      
      console.log('Sending email with params:', templateParams);
      console.log('Service ID:', serviceId);
      console.log('Template ID:', templateId);
      
      const result = await emailjs.send(
        serviceId,
        templateId,
        templateParams
      );
      
      console.log('Email sent successfully:', result);

      setIsSubmitting(false);
      
      // Weiterleitung zur Bestätigungsseite
      navigate('/termin-bestaetigung');
    } catch (error: any) {
      console.error('Error sending email:', error);
      console.error('Error details:', {
        status: error?.status,
        text: error?.text,
        message: error?.message
      });
      setIsSubmitting(false);
      
      // Show alert to user
      alert(`Fehler beim Senden der Email: ${error?.text || error?.message || 'Unbekannter Fehler'}`);
      
      // Don't redirect on error
      return;
    }
  };

  return (
    <Layout>
      <section className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-24 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center justify-center px-6 py-2 mb-6 text-sm font-semibold text-blue-700 bg-blue-100 rounded-full"
              >
                <Star className="w-4 h-4 mr-2" />
                PREMIUM BERATUNGSGESPRÄCH • WERT 297 CHF
              </motion.div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="block text-gray-900">Dein Weg zur</span>
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
                  digitalen Exzellenz
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                30 Minuten Premium-Beratung, die dein Business transformieren.
                <span className="block mt-2 font-semibold text-gray-800">
                  100% kostenlos. 100% maßgeschneidert. 100% wertvoll.
                </span>
              </p>

              {/* Urgency Banner */}
              <motion.div
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full shadow-lg mb-8"
              >
                <Clock className="w-5 h-5 mr-2" />
                <span className="font-semibold">Sichere dir jetzt dein kostenloses Beratungsgespräch!</span>
              </motion.div>
            </div>

            {/* Main Form */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8 lg:p-12">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Kontaktiere uns</h3>
                    <p className="text-gray-600">Erzähl uns von dir und deinem Projekt</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">E-Mail *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300"
                        placeholder="max@beispiel.ch"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon (optional)</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300"
                        placeholder="+41 76 123 45 67"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-4">Hauptthema *</label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {topics.map((topic) => (
                        <label key={topic.value} className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="topic"
                            value={topic.value}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                            formData.topic === topic.value 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-blue-300'
                          }`}>
                            <div className="font-semibold text-gray-900 mb-1">{topic.label}</div>
                            <div className="text-sm text-gray-600">{topic.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Deine größte Herausforderung</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 resize-none"
                      placeholder="Beschreibe kurz, was dich aktuell beschäftigt..."
                    />
                  </div>

                  {/* Was dich erwartet */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <h4 className="font-bold text-green-800 mb-4 flex items-center">
                      <Sparkles className="w-5 h-5 mr-2" />
                      Was dich erwartet:
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                        <div>
                          <div className="font-medium text-green-800">Individuelle Strategieberatung</div>
                          <div className="text-sm text-green-700">Maßgeschneiderte Lösungen für deine Herausforderungen</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                        <div>
                          <div className="font-medium text-green-800">Konkrete Umsetzungsroadmap</div>
                          <div className="text-sm text-green-700">Schritt-für-Schritt Plan mit Zeitschätzungen</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                        <div>
                          <div className="font-medium text-green-800">Technologie-Empfehlungen</div>
                          <div className="text-sm text-green-700">Optimale Tools und Lösungen für dein Business</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                        <div>
                          <div className="font-medium text-green-800">Potenzialanalyse</div>
                          <div className="text-sm text-green-700">Identifikation von Effizienzsteigerungen</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <button
                      type="submit"
                      disabled={!canSubmit || isSubmitting}
                      className="group relative px-12 py-4 text-xl font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <span className="relative z-10 flex items-center">
                        {isSubmitting ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              className="w-6 h-6 border-2 border-white border-t-transparent rounded-full mr-3"
                            />
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            Jetzt Beratung anfragen
                            <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </span>
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600"
                        initial={{ x: "-100%" }}
                        whileHover={{ x: 0 }}
                        transition={{ duration: 0.3 }}
                      />
                    </button>
                  </div>
                </motion.div>
              </form>
            </motion.div>

            {/* Trust Signals */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-12 text-center"
            >
              <p className="text-gray-600 mb-4">
                Vertraut von 50+ Schweizer Unternehmen
              </p>
              <div className="flex justify-center items-center space-x-8 opacity-60">
                <div className="text-lg font-semibold">★★★★★</div>
                <div className="text-sm">5.0 Bewertung</div>
                <div className="text-sm">98% Weiterempfehlung</div>
                <div className="text-sm">24h Response Time</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default TerminBuchen;